import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Grid, Typography, Stack, TableContainer, Table, TableBody, TableRow, TableCell, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { dateFormatter, formatAddress, formatCityState } from 'utils/data-manipulation/valueFormatter'
import { setDecimalPrecision } from 'utils/helpers'
import { aggregateTicketMaterials } from 'utils/aggregateTicketMaterials'
import { PRINT_DOCUMENT_TYPES } from 'utils/constants'

const TableHeaderCell = styled(TableCell)(() => ({ fontSize: '10px', fontWeight: 600 }))
const NumberTableHeaderCell = styled(TableHeaderCell)(() => ({ paddingLeft: '8px', paddingRight: '8px' }))
const TableBodyCell = styled(TableCell)(() => ({ fontSize: '8px' }))
const NumberTableBodyCell = styled(TableBodyCell)(() => ({ paddingLeft: '8px', paddingRight: '8px' }))

const TicketRows = ({
  type,
  ticket,
  headers,
  priceSheet,
  priceEntriesByPurchaseOrderId,
  defaultPriceSheet,
  defaultPriceType,
  showDeductions,
  showWeightDecimals,
  showMaterialDeductionNotes,
  isPayment,
  consumerWeights,
  pricePrepend,
  showTruckTotal,
}) => {
  const { settings, defaultYardUm } = useSelector(st => st.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}

  const { materials = [], totalData } = aggregateTicketMaterials({
    tickets: [ticket],
    priceSheet,
    priceEntriesByPurchaseOrderId,
    isSummary: false,
    defaultPriceSheet,
    defaultPriceType,
    consumerWeights,
    pricePrepend,
    disableUmDropdowns,
    defaultYardUm,
  })
  const ticketSubtotal = ticket.subTotal || 0
  const ticketChargeback = ticket.freightCost?.chargeBack || 0
  const truckGross = ticket.truckWeight?.gross?.amount || 0
  const truckTare = ticket.truckWeight?.tare?.amount || 0
  const truckNet = Number(truckGross) - Number(truckTare)

  const formattedTicketChargeback =
    type !== PRINT_DOCUMENT_TYPES.INVOICE || ticketChargeback < 0
      ? `(${pricePrepend}${setDecimalPrecision(Math.abs(ticketChargeback), { decimalPrecisionOverride: 2 })})`
      : `${pricePrepend}${setDecimalPrecision(Math.abs(ticketChargeback), { decimalPrecisionOverride: 2 })}`

  return materials.length === 0 ? null : (
    <React.Fragment key={ticket.id}>
      <TableRow>
        <TableCell colSpan={headers.length}>
          <Stack direction="row" spacing={3} alignItems="center" sx={{ colSpan: 6 }}>
            <Typography>Ticket #{ticket.tag || ticket.ticketId}</Typography>
            <Typography noWrap variant="caption">
              Ship Date: {dateFormatter(ticket.effectiveDate || ticket.createdAt || ticket.createdDate)}
            </Typography>
            {isPayment ? (
              <>
                <Typography noWrap variant="caption">
                  PO #: {ticket?.orderNumber}
                </Typography>
                <Typography noWrap variant="caption">
                  BOL #: {ticket?.billOfLading}
                </Typography>
                {!!ticket?.address && (
                  <Typography noWrap variant="caption">
                    Location: {formatAddress(ticket?.address)} {formatCityState(ticket?.address)}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography noWrap variant="caption">
                  Mill Scale Ticket #: {ticket?.millScaleNumber}
                </Typography>
                <Typography noWrap variant="caption">
                  Booking Ref #: {ticket?.contractInfo?.bookingRef}
                </Typography>
                <Typography noWrap variant="caption">
                  Container #: {ticket?.transportationInfo?.container}
                </Typography>
              </>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        {headers.map((header, index) =>
          index > 0 ? (
            <NumberTableHeaderCell align="right" key={header}>
              {header}
            </NumberTableHeaderCell>
          ) : (
            <TableHeaderCell align="left" key={header}>
              {header}
            </TableHeaderCell>
          ),
        )}
      </TableRow>

      {materials.map(material => (
        <React.Fragment key={material.id}>
          <TableRow key={material.id} sx={{ padding: 0 }}>
            <TableBodyCell>{material.materialName}</TableBodyCell>
            <NumberTableBodyCell align="right">{material.gross}</NumberTableBodyCell>
            <NumberTableBodyCell align="right">{material.tare}</NumberTableBodyCell>
            {showDeductions && <NumberTableBodyCell align="right">{material.deductions}</NumberTableBodyCell>}
            <NumberTableBodyCell align="right">{material.net}</NumberTableBodyCell>
            <NumberTableBodyCell align="right">{material.price}</NumberTableBodyCell>
            <NumberTableBodyCell align="right">{material.total}</NumberTableBodyCell>
          </TableRow>
          {showMaterialDeductionNotes &&
            material?.deductionNotes?.length > 0 &&
            material?.deductionNotes?.map(deduction => (
              <TableRow key={deduction.id}>
                <TableBodyCell colSpan={6}>
                  <Box pl={1}>
                    {deduction && (
                      <>
                        <b>Notes</b>: {deduction}
                      </>
                    )}
                  </Box>
                </TableBodyCell>
              </TableRow>
            ))}
        </React.Fragment>
      ))}
      {ticketChargeback ? (
        <TableRow key="chargeback-summary">
          <TableBodyCell>Freight Charge</TableBodyCell>
          <NumberTableBodyCell align="right" />
          <NumberTableBodyCell align="right" />
          {showDeductions && <NumberTableBodyCell align="right" />}
          <NumberTableBodyCell align="right" />
          <NumberTableBodyCell align="right" />
          <NumberTableBodyCell align="right">{formattedTicketChargeback}</NumberTableBodyCell>
        </TableRow>
      ) : null}
      <TableRow key={`${ticket.id}-total`} sx={{ backgroundColor: 'lightgray' }}>
        <TableBodyCell sx={{ fontWeight: 600, borderBottom: 'none' }}>
          {type === PRINT_DOCUMENT_TYPES.BILL ? 'SUBTOTAL' : 'TOTAL'}
        </TableBodyCell>
        <NumberTableBodyCell sx={{ fontWeight: 600 }} align="right">
          {setDecimalPrecision(totalData.gross, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableBodyCell>
        <NumberTableBodyCell sx={{ fontWeight: 600 }} align="right">
          {setDecimalPrecision(totalData.tare, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableBodyCell>
        {showDeductions && <NumberTableBodyCell sx={{ fontWeight: 600 }} align="right" />}
        <NumberTableBodyCell sx={{ fontWeight: 600 }} align="right">
          {setDecimalPrecision(totalData.net, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableBodyCell>
        <NumberTableBodyCell sx={{ fontWeight: 600 }} align="right" />
        <NumberTableBodyCell sx={{ fontWeight: 600 }} align="right">
          {pricePrepend}
          {setDecimalPrecision(
            defaultPriceType === 'buyPrice'
              ? ticketSubtotal
              : Number(totalData.total || 0) + Number(ticketChargeback || 0),
            {
              decimalPrecisionOverride: 2,
            },
          )}
        </NumberTableBodyCell>
      </TableRow>
      {showTruckTotal && (
        <TableRow key={`${ticket.id}-truck-total`}>
          <TableBodyCell>Truck Total</TableBodyCell>
          <NumberTableBodyCell align="right">
            {setDecimalPrecision(truckGross, {
              removeTrailingZeros: true,
              decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
            })}
          </NumberTableBodyCell>
          <NumberTableBodyCell align="right">
            {setDecimalPrecision(truckTare, {
              removeTrailingZeros: true,
              decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
            })}
          </NumberTableBodyCell>
          <NumberTableBodyCell />
          <NumberTableBodyCell align="right">
            {setDecimalPrecision(truckNet, {
              removeTrailingZeros: true,
              decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
            })}
          </NumberTableBodyCell>
          <NumberTableBodyCell />
          <NumberTableBodyCell />
        </TableRow>
      )}
      <TableRow sx={{ height: '16px' }} />
    </React.Fragment>
  )
}

const PrintTicketsGrid = ({
  type,
  title,
  headers,
  tickets,
  priceSheet,
  priceEntriesByPurchaseOrderId,
  defaultPriceSheet,
  defaultPriceType,
  showDeductions,
  showWeightDecimals,
  showMaterialDeductionNotes,
  isPayment,
  consumerWeights,
  pricePrepend,
  showTruckTotal,
}) => (
  <Grid>
    <Stack direction="row" mb={1}>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
    </Stack>

    <TableContainer sx={{ overflow: 'hidden' }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableBody>
          {(tickets || []).map(ticket => (
            <TicketRows
              type={type}
              ticket={ticket}
              headers={headers}
              priceSheet={priceSheet}
              priceEntriesByPurchaseOrderId={priceEntriesByPurchaseOrderId}
              defaultPriceSheet={defaultPriceSheet}
              defaultPriceType={defaultPriceType}
              showDeductions={showDeductions}
              showWeightDecimals={showWeightDecimals}
              showMaterialDeductionNotes={showMaterialDeductionNotes}
              isPayment={isPayment}
              consumerWeights={consumerWeights}
              pricePrepend={pricePrepend}
              showTruckTotal={showTruckTotal}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Grid>
)

PrintTicketsGrid.propTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string,
  headers: PropTypes.array,
  tickets: PropTypes.array,
  priceSheet: PropTypes.object,
  priceEntriesByPurchaseOrderId: PropTypes.object,
  defaultPriceSheet: PropTypes.object,
  defaultPriceType: PropTypes.string,
  showDeductions: PropTypes.bool,
  showWeightDecimals: PropTypes.bool,
  showMaterialDeductionNotes: PropTypes.bool,
  isPayment: PropTypes.bool,
  consumerWeights: PropTypes.array,
  pricePrepend: PropTypes.string,
  showTruckTotal: PropTypes.bool,
}

PrintTicketsGrid.defaultProps = {
  title: '',
  headers: [],
  tickets: [],
  priceSheet: {},
  priceEntriesByPurchaseOrderId: {},
  defaultPriceSheet: {},
  defaultPriceType: 'buyPrice',
  showDeductions: false,
  showWeightDecimals: false,
  showMaterialDeductionNotes: false,
  isPayment: false,
  consumerWeights: [],
  pricePrepend: '$',
  showTruckTotal: false,
}

export default PrintTicketsGrid
